import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, Edit, Trash2, Eye, EyeOff, Settings, Save, X, Upload,
  FolderPlus, Image as ImageIcon, ChevronDown, ChevronRight, Globe, Star,
  ArrowUp, ArrowDown, Download, Database
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPlacements() {
  const { token, user } = useAuth();
  const isOfficeAdmin = user?.office_id && ['admin_ops','bureau'].includes(user?.role);
  const [canEdit, setCanEdit] = useState(!isOfficeAdmin);
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // State
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    reference: '',
    name: '',
    category_id: '',
    subcategory: '',
    price_min: '',
    price_max: ''
  });
  
  // Dialogs
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // New category form
  const [newCategory, setNewCategory] = useState({
    title: '',
    slug: '',
    description: '',
    image_url: '',
    link_action: '',
    order: 0,
    is_active: true,
    subcategories: ['']
  });

  const fetchData = useCallback(async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/placements/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/placements/products`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            search: filters.name || filters.reference,
            category_id: filters.category_id || undefined 
          }
        })
      ]);
      
      setCategories(categoriesRes.data);
      const productsData = Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data.products || []);
      setProducts(productsData);
    } catch (error) {
      console.error('Failed to fetch placements:', error);
      toast.error('Erreur', { description: 'Impossible de charger les données' });
    } finally {
      setLoading(false);
    }
  }, [token, filters.name, filters.reference, filters.category_id]);

  useEffect(() => {
    fetchData();
    if (isOfficeAdmin) {
      axios.get(`${API}/offices/my-permissions`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setCanEdit(res.data?.can_manage_placements ?? false)).catch(() => {});
    }
  }, [fetchData, isOfficeAdmin, token]);

  // Category handlers
  const handleSaveCategory = async () => {
    try {
      const dataToSend = {
        ...newCategory,
        subcategories: newCategory.subcategories.filter(s => s.trim() !== '')
      };
      
      if (editingCategory) {
        await axios.put(`${API}/placements/categories/${editingCategory.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API}/placements/categories`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie créée');
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors de la sauvegarde' });
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({ 
      ...category,
      subcategories: category.subcategories?.length > 0 ? category.subcategories : ['']
    });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await axios.delete(`${API}/placements/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Impossible de supprimer' });
    }
  };

  const resetCategoryForm = () => {
    setNewCategory({
      title: '',
      slug: '',
      description: '',
      image_url: '',
      link_action: '',
      order: 0,
      is_active: true,
      subcategories: ['']
    });
  };

  const addSubcategory = () => {
    setNewCategory(prev => ({
      ...prev,
      subcategories: [...prev.subcategories, '']
    }));
  };

  const updateSubcategory = (index, value) => {
    setNewCategory(prev => ({
      ...prev,
      subcategories: prev.subcategories.map((s, i) => i === index ? value : s)
    }));
  };

  const removeSubcategory = (index) => {
    setNewCategory(prev => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index)
    }));
  };

  const [uploadingCatImage, setUploadingCatImage] = useState(false);
  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingCatImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setNewCategory(prev => ({ ...prev, image_url: fullUrl }));
      toast.success('Image uploadee');
    } catch (err) {
      toast.error('Erreur upload', { description: err.response?.data?.detail || 'Echec upload' });
    } finally {
      setUploadingCatImage(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await axios.delete(`${API}/placements/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produit supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Impossible de supprimer' });
    }
  };

  const moveCategoryOrder = async (index, direction) => {
    const newList = [...categories];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    setCategories(newList);
    try {
      await axios.put(`${API}/placements/categories/reorder`, 
        { order: newList.map(c => c.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordre mis à jour');
    } catch (err) { 
      console.error('Reorder error:', err);
      toast.error('Erreur de réordonnancement'); 
      fetchData();
    }
  };

  const moveProductOrder = async (index, direction) => {
    const newList = [...products];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    setProducts(newList);
    try {
      await axios.put(`${API}/placements/products/reorder`,
        { order: newList.map(p => p.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordre mis à jour');
    } catch (err) { 
      console.error('Reorder error:', err);
      toast.error('Erreur de réordonnancement'); 
      fetchData();
    }
  };

  const handleSearch = () => {
    fetchData();
  };

  // Placement backup (export / import) — super_admin only
  const isSuperAdmin = user?.role === 'super_admin';
  const importInputRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleExportBackup = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/placements/backup/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sauvegarde-placements-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const meta = res.data?._meta || {};
      toast.success('Sauvegarde exportée', {
        description: `${meta.categories_count || 0} catégorie(s) et ${meta.products_count || 0} placement(s)`
      });
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || "Échec de l'export" });
    } finally {
      setExporting(false);
    }
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!window.confirm('Importer cette sauvegarde ? Les catégories et placements seront ajoutés ou mis à jour (aucune suppression).')) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const res = await axios.post(`${API}/placements/backup/import`, parsed, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data || {};
      toast.success('Sauvegarde importée', {
        description: `Catégories: ${d.categories_created || 0} créée(s), ${d.categories_updated || 0} mise(s) à jour • Placements: ${d.products_created || 0} créé(s), ${d.products_updated || 0} mis à jour`
      });
      fetchData();
    } catch (error) {
      const detail = error instanceof SyntaxError
        ? 'Fichier JSON invalide'
        : (error.response?.data?.detail || "Échec de l'import");
      toast.error('Erreur', { description: detail });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Backup bar (Super Admin only) */}
        {isSuperAdmin && (
          <Card className="bg-card/80 backdrop-blur border-border/50" data-testid="placements-backup-bar">
            <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600/15 flex items-center justify-center flex-shrink-0">
                  <Database className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Sauvegarde des placements</h3>
                  <p className="text-xs text-muted-foreground">Exportez ou réimportez les catégories et placements</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={handleExportBackup}
                  disabled={exporting}
                  data-testid="export-placements-btn"
                >
                  <Download className="w-4 h-4" />
                  {exporting ? 'Export...' : 'Exporter la sauvegarde'}
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                  onClick={() => importInputRef.current?.click()}
                  disabled={importing}
                  data-testid="import-placements-btn"
                >
                  <Upload className="w-4 h-4" />
                  {importing ? 'Import...' : 'Importer une sauvegarde'}
                </Button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleImportBackup}
                  data-testid="import-placements-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Layout: Categories on left, Products on right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Categories Section - Left Side */}
          <div className="lg:col-span-1">
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg">Catégories</CardTitle>
                {canEdit && (
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => { setEditingCategory(null); resetCategoryForm(); setCategoryDialogOpen(true); }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Créer une catégorie
                </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {categories.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-8">
                    Aucune catégorie créée
                  </p>
                ) : (
                  categories.map((cat, catIdx) => (
                    <div 
                      key={cat.id} 
                      className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-background/50 hover:bg-muted/30 transition-colors"
                    >
                      {/* Order arrows */}
                      {canEdit && (
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveCategoryOrder(catIdx, -1)} disabled={catIdx === 0} className="p-0.5 rounded hover:bg-muted disabled:opacity-20 transition-colors" data-testid={`cat-up-${catIdx}`}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => moveCategoryOrder(catIdx, 1)} disabled={catIdx === categories.length - 1} className="p-0.5 rounded hover:bg-muted disabled:opacity-20 transition-colors" data-testid={`cat-down-${catIdx}`}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      )}
                      {/* Category Image */}
                      <div className="w-16 h-16 rounded-lg bg-muted/30 overflow-hidden flex-shrink-0">
                        {cat.image_url ? (
                          <img 
                            src={cat.image_url} 
                            alt={cat.title} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
                          </div>
                        )}
                      </div>
                      
                      {/* Category Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">{cat.title}</h4>
                        <p className="text-xs text-muted-foreground">{cat.product_count || 0} produit(s)</p>
                        {cat.subcategories?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {cat.subcategories.length} sous-catégorie(s)
                          </p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      {canEdit && (
                      <div className="flex gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8"
                          onClick={() => handleEditCategory(cat)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteCategory(cat.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Products Section - Right Side */}
          <div className="lg:col-span-2">
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <CardTitle className="text-lg">Produits</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="gap-1"
                    >
                      <Settings className="w-4 h-4" />
                      Paramètres pays
                    </Button>
                    {canEdit && (
                    <Button 
                      size="sm" 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate('/admin/placements/new')}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Créer un produit
                    </Button>
                    )}
                  </div>
                </div>
                
                {/* Filters */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-4">
                  <Input
                    placeholder="Référence"
                    value={filters.reference}
                    onChange={(e) => setFilters(prev => ({ ...prev, reference: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input
                    placeholder="Nom du produit"
                    value={filters.name}
                    onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Select 
                    value={filters.category_id || "all"} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, category_id: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Sous-catégorie"
                    value={filters.subcategory}
                    onChange={(e) => setFilters(prev => ({ ...prev, subcategory: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input
                    placeholder="Prix min"
                    type="number"
                    value={filters.price_min}
                    onChange={(e) => setFilters(prev => ({ ...prev, price_min: e.target.value }))}
                    className="h-9 text-sm"
                  />
                  <Input
                    placeholder="Prix max"
                    type="number"
                    value={filters.price_max}
                    onChange={(e) => setFilters(prev => ({ ...prev, price_max: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleSearch}>
                    <Search className="w-4 h-4 mr-1" />
                    RECHERCHER
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-y border-border/50">
                      <tr className="text-left text-muted-foreground text-xs uppercase">
                        <th className="p-3 w-12">Ordre</th>
                        <th className="p-3">Référence</th>
                        <th className="p-3">Produit</th>
                        <th className="p-3">Catégorie</th>
                        <th className="p-3">Sous-cat.</th>
                        <th className="p-3">Prix</th>
                        <th className="p-3">Rentabilité</th>
                        <th className="p-3">Durée</th>
                        <th className="p-3 text-center">Crowdf.</th>
                        <th className="p-3 text-center">Actif</th>
                        <th className="p-3 text-center">Une</th>
                        <th className="p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, index) => (
                        <tr key={product.id} className="border-b border-border/30 hover:bg-muted/20">
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground text-xs w-4">{index + 1}</span>
                              {canEdit && (
                              <div className="flex flex-col gap-0">
                                <button onClick={() => moveProductOrder(index, -1)} disabled={index === 0} className="p-0.5 rounded hover:bg-muted disabled:opacity-20" data-testid={`prod-up-${index}`}>
                                  <ArrowUp className="w-3 h-3" />
                                </button>
                                <button onClick={() => moveProductOrder(index, 1)} disabled={index === products.length - 1} className="p-0.5 rounded hover:bg-muted disabled:opacity-20" data-testid={`prod-down-${index}`}>
                                  <ArrowDown className="w-3 h-3" />
                                </button>
                              </div>
                              )}
                            </div>
                          </td>
                          <td className="p-3 font-mono text-xs">{product.reference}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {product.images?.[0] ? (
                                <img 
                                  src={product.images[0]} 
                                  alt="" 
                                  className="w-8 h-8 rounded object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded bg-muted/30 flex items-center justify-center">
                                  <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
                                </div>
                              )}
                              <span className="font-medium truncate max-w-[150px]">{product.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground">{product.category_name}</td>
                          <td className="p-3 text-muted-foreground">{product.subcategory || '-'}</td>
                          <td className="p-3">{product.min_investment?.toLocaleString('fr-FR')} €</td>
                          <td className="p-3">
                            <Badge className="bg-green-500/20 text-green-400 border-0">
                              {product.rentability_rate}% {product.rentability_period?.charAt(0)}
                            </Badge>
                          </td>
                          <td className="p-3">{product.duration_months} mois</td>
                          <td className="p-3 text-center">
                            <Checkbox checked={product.is_crowdfunding} disabled />
                          </td>
                          <td className="p-3 text-center">
                            <Checkbox checked={product.is_active} disabled />
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              size="icon"
                              variant="ghost"
                              className={`h-7 w-7 ${product.is_featured ? 'text-yellow-500' : 'text-muted-foreground'}`}
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/placements/products/${product.id}`, {
                                    is_featured: !product.is_featured
                                  }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success(product.is_featured ? 'Retiré de la une' : 'Mis à la une');
                                  fetchData();
                                } catch { toast.error('Erreur'); }
                              }}
                              data-testid={`featured-product-${product.id}`}
                            >
                              <Star className={`w-4 h-4 ${product.is_featured ? 'fill-current' : ''}`} />
                            </Button>
                          </td>
                          <td className="p-3">
                            {canEdit ? (
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => navigate(`/admin/placements/${product.id}`)}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost"
                                className={`h-7 w-7 ${product.is_active ? 'text-green-500' : 'text-red-500'}`}
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/placements/products/${product.id}`, { is_active: !product.is_active }, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success(product.is_active ? 'Produit masqué' : 'Produit visible');
                                    fetchData();
                                  } catch { toast.error('Erreur'); }
                                }}
                                data-testid={`toggle-active-${product.id}`}
                              >
                                {product.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteProduct(product.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Lecture seule</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {products.length === 0 && (
                        <tr>
                          <td colSpan={11} className="p-8 text-center text-muted-foreground">
                            Aucun produit
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="p-3 border-t border-border/50 text-sm text-muted-foreground">
                  {products.length} produit(s)
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Category Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Modifier la catégorie' : 'Créer une catégorie'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Title & Slug */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input 
                    value={newCategory.title} 
                    onChange={(e) => setNewCategory(prev => ({ 
                      ...prev, 
                      title: e.target.value, 
                      slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                    }))}
                    placeholder="Ex: Offres de Placement Livret"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL formaté</Label>
                  <Input 
                    value={newCategory.slug} 
                    onChange={(e) => setNewCategory(prev => ({ ...prev, slug: e.target.value }))}
                    placeholder="offres-placement-livret"
                    className="bg-muted/30"
                  />
                </div>
              </div>

              {/* Link Action */}
              <div className="space-y-2">
                <Label>Lien vers action</Label>
                <Select 
                  value={newCategory.link_action || 'none'} 
                  onValueChange={(v) => setNewCategory(prev => ({ ...prev, link_action: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    <SelectItem value="crypto">Crypto</SelectItem>
                    <SelectItem value="bourse">Bourse</SelectItem>
                    <SelectItem value="epargne">Épargne</SelectItem>
                    <SelectItem value="immobilier">Immobilier</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex gap-4">
                  <div className="w-32 h-32 rounded-lg border-2 border-dashed border-border/50 bg-muted/20 flex items-center justify-center overflow-hidden">
                    {newCategory.image_url ? (
                      <img 
                        src={newCategory.image_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-2">
                        <Upload className="w-8 h-8 mx-auto text-muted-foreground/50 mb-1" />
                        <span className="text-xs text-muted-foreground">Aucune image</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input 
                      value={newCategory.image_url || ''} 
                      onChange={(e) => setNewCategory(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="URL de l'image..."
                    />
                    <div className="flex gap-2 items-center">
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm transition-colors">
                        <Upload className="w-4 h-4" />
                        {uploadingCatImage ? 'Upload...' : 'Uploader un fichier'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleCategoryImageUpload}
                          disabled={uploadingCatImage}
                        />
                      </label>
                      <span className="text-xs text-muted-foreground">ou collez une URL</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Translations Section */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <Label>Traductions</Label>
                </div>
                <div className="p-4 rounded-lg border border-border/50 bg-muted/10">
                  <p className="text-sm text-muted-foreground">
                    Les traductions seront gérées dans une version future.
                  </p>
                </div>
              </div>

              {/* Subcategories */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Sous-catégories</Label>
                  <Button size="sm" variant="outline" onClick={addSubcategory}>
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {newCategory.subcategories.map((subcat, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={subcat}
                        onChange={(e) => updateSubcategory(index, e.target.value)}
                        placeholder={`Sous-catégorie ${index + 1}`}
                      />
                      {newCategory.subcategories.length > 1 && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-red-500"
                          onClick={() => removeSubcategory(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Switch */}
              <div className="flex items-center gap-2">
                <Switch 
                  checked={newCategory.is_active} 
                  onCheckedChange={(v) => setNewCategory(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Catégorie active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Annuler
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveCategory}>
                <Save className="w-4 h-4 mr-2" />
                {editingCategory ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
